import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight, Check, Instagram, MessageCircle, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import FlipAlbum from "../components/FlipAlbum";
import IntroSplash from "../components/IntroSplash";
import SiteHeader from "../components/SiteHeader";
import { api, getAssetUrl } from "../lib/api";

const reveal = { initial: { opacity: 0, y: 28 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, amount: 0.2 }, transition: { duration: 0.7 } };

const instagramAccounts = [
  {
    handle: "@eceozokphotography",
    label: "Ece Özok Photography",
    qr: "https://customer-assets-lxgj4vgw.emergentagent.net/job_film-grain-atelier/artifacts/m0rqel68_6x6%20QR%20ECE%20siyah%20%281%29.png",
    url: "https://www.instagram.com/eceozokphotography",
  },
  {
    handle: "@eylulfotografcilik_sercanozok",
    label: "Eylül Fotoğrafçılık · Sercan Özok",
    qr: "https://customer-assets-lxgj4vgw.emergentagent.net/job_film-grain-atelier/artifacts/f5ajkapa_6x6%20QR%20SERCAN%20siyah%20%281%29.png",
    url: "https://www.instagram.com/eylulfotografcilik_sercanozok",
  },
];

export default function HomePage() {
  const [showIntro, setShowIntro] = useState(() => localStorage.getItem("eylul-intro-seen") !== "true");
  const [site, setSite] = useState(null);
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    window.history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
    api.get("/site").then(({ data }) => setSite(data)).catch(() => toast.error("İçerikler yüklenirken bir sorun oluştu."));
  }, []);

  const closeIntro = () => {
    localStorage.setItem("eylul-intro-seen", "true");
    setShowIntro(false);
    window.scrollTo(0, 0);
  };

  const submitContact = async (event) => {
    event.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      setForm({ name: "", phone: "", message: "" });
      toast.success("Mesajınız iletildi. En kısa sürede size döneceğiz.");
    } catch {
      toast.error("Mesajınız şu an iletilemedi. Lütfen tekrar deneyin.");
    } finally {
      setSending(false);
    }
  };

  if (!site) {
    return <div className="site-loading" data-testid="site-loading-state">Eylül Fotoğrafçılık</div>;
  }

  const { content, packages } = site;
  const heroImage = getAssetUrl(content.hero_images[0]);
  const whatsapp = `https://wa.me/90${content.contact.whatsapp.replace(/\D/g, "").replace(/^0/, "")}`;

  return (
    <main className="dc-home">
      <IntroSplash visible={showIntro} onComplete={closeIntro} />
      <SiteHeader />
      <section className="hero-section" id="anasayfa" data-testid="hero-section">
        <motion.div {...reveal} className="hero-copy">
          <span className="eyebrow">HİKÂYEMİZ</span>
          <h1>Anılarınızı<br /><em>Ölümsüzleştiriyoruz</em></h1>
          <p>{content.about}</p>
          <div className="hero-actions"><a className="hero-scroll" href="#hikayemiz" data-testid="hero-discover-link">Daha fazla oku <ArrowDown size={17} /></a><a className="hero-secondary" href="#paketler" data-testid="hero-story-link">Paketler</a></div>
        </motion.div>
        <div className="hero-media">
          <img className="hero-image" src={heroImage} alt="Eylül Fotoğrafçılık çift çekimi" data-testid="hero-background-image" />
          <span className="hero-media-note">EYLÜL FOTOĞRAFÇILIK<br />WEDDING EDITION</span>
        </div>
      </section>

      <section className="about-section section-shell" id="hikayemiz" data-testid="about-section">
        <motion.div {...reveal} className="about-copy">
          <span className="eyebrow">BİR KAREDEN DAHASI</span>
          <h2>Işığın peşinde,<br />sizin hikâyenizde.</h2>
          <p>{content.about}</p>
          <a className="text-link" href="#iletisim" data-testid="about-contact-link">Birlikte planlayalım <ArrowUpRight size={18} /></a>
        </motion.div>
        <motion.div {...reveal} className="about-visuals">
          <img src={getAssetUrl(content.hero_images[1] || content.hero_images[0])} alt="Romantik çift çekimi" loading="lazy" data-testid="about-primary-image" />
          <div className="about-quote" data-testid="about-quote-text">“Zamanın ötesinde bir his.”</div>
        </motion.div>
      </section>

      <section className="album-section" id="album" data-testid="album-section">
        <motion.div {...reveal} className="album-heading section-shell">
          <div><span className="eyebrow">SAYFALARI ÇEVİRİN</span><h2>Bir hikâye,<br /><em>bir ömür.</em></h2></div>
          <p>Dokunun, sürükleyin ve düğün gününüzün zamansız atmosferini her sayfada yeniden yaşayın.</p>
        </motion.div>
        <FlipAlbum images={content.album_images} />
      </section>

      <section className="packages-section section-shell" id="paketler" data-testid="packages-section">
        <motion.div {...reveal} className="section-intro"><span className="eyebrow">AKTİF ÇEKİM KOLEKSİYONU</span><h2>Size özel<br /><em>fırsatlar.</em></h2><p>Her ayrıntısı, gününüze uygun şekilde tasarlandı.</p></motion.div>
        <div className="campaign-grid">
          {packages.map((pack, index) => (
            <article className="campaign-card" key={pack.name} data-testid={`package-card-${index + 1}`}>
              <div className="campaign-photo">
                <img src={getAssetUrl(pack.image)} alt={`${pack.name} paket çekimi`} loading="eager" fetchPriority="high" data-testid={`package-image-${index + 1}`} />
                <span className="campaign-badge">{index % 3 === 0 ? "ÖZEL" : index % 3 === 1 ? "SEÇKİ" : "PREMİUM"}</span>
              </div>
              <div className="campaign-content">
                <div className="campaign-heading">
                  <span>ÇEKİM PAKETİ · {String(index + 1).padStart(2, "0")}</span>
                  <h3>{pack.name}</h3>
                  <p>Hikâyenizin en doğal hâlini kaydetmek için seçildi.</p>
                </div>
                <ul className="campaign-items">{pack.items.map((item) => <li key={item}><Check size={13} />{item}</li>)}</ul>
                <div className="campaign-footer">
                  <div>
                    <span>PAKET BEDELİ</span>
                    <div className="package-price" data-testid={`package-price-${index + 1}`}>{pack.price || "Fiyat için iletişime geçin"}</div>
                    {pack.installment_text && <div className="package-installment" data-testid={`package-installment-${index + 1}`}>{pack.installment_text}</div>}
                  </div>
                  <Link to="/galeri" data-testid={`package-catalog-link-${index + 1}`}>Galeriyi gör <ArrowUpRight size={16} /></Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="contact-section" id="iletisim" data-testid="contact-section">
        <div className="section-shell contact-grid">
          <motion.div {...reveal} className="contact-copy"><span className="eyebrow">BİRLİKTE BAŞLAYALIM</span><h2>Hikâyenizi<br /><em>dinleyelim.</em></h2><a href={whatsapp} target="_blank" rel="noreferrer" className="whatsapp-action" data-testid="whatsapp-contact-button"><MessageCircle size={19} /> WhatsApp’tan yazın <ArrowUpRight size={17} /></a><a className="phone-link" href={`tel:${content.contact.phone}`} data-testid="office-phone-link"><Phone size={16} /> {content.contact.phone}</a></motion.div>
          <motion.form {...reveal} className="contact-form" onSubmit={submitContact} data-testid="contact-form">
            <label>Adınız<input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} required data-testid="contact-name-input" /></label>
            <label>Telefonunuz<input value={form.phone} onChange={(event) => setForm({ ...form, phone: event.target.value })} required data-testid="contact-phone-input" /></label>
            <label>Notunuz<textarea value={form.message} onChange={(event) => setForm({ ...form, message: event.target.value })} required data-testid="contact-message-input" /></label>
            <button disabled={sending} type="submit" data-testid="contact-form-submit-button">{sending ? "Gönderiliyor..." : "Mesajı gönder"} <ArrowUpRight size={17} /></button>
          </motion.form>
        </div>
        <div className="maps-grid section-shell">
          <article data-testid="head-office-map"><div><span className="eyebrow">MERKEZ OFİS · GÖLBAŞI</span><p>{content.contact.office}</p></div><iframe title="Eylül Fotoğrafçılık Merkez Ofis Haritası" src="https://www.google.com/maps?q=Vole+Garden+Tulumtaş+Gölbaşı&output=embed" loading="lazy" data-testid="head-office-map-embed" /></article>
          <article data-testid="branch-office-map"><div><span className="eyebrow">ŞUBE · POLATLI</span><p>{content.contact.branch}</p></div><iframe title="Eylül Fotoğrafçılık Polatlı Şube Haritası" src="https://www.google.com/maps?q=Gazi+Mahallesi+Adnan+Menderes+Caddesi+Polatlı&output=embed" loading="lazy" data-testid="branch-office-map-embed" /></article>
        </div>
        <section className="instagram-section section-shell" data-testid="instagram-section">
          <div className="instagram-heading"><span className="eyebrow">SOSYAL MEDYA</span><h2>Güncel kareler için<br /><em>bizi takip edin.</em></h2></div>
          <div className="instagram-accounts">
            {instagramAccounts.map((account, index) => <a className="instagram-account" href={account.url} target="_blank" rel="noreferrer" key={account.handle} data-testid={`instagram-account-link-${index + 1}`}><img src={account.qr} alt={`${account.label} Instagram QR kodu`} loading="lazy" data-testid={`instagram-qr-image-${index + 1}`} /><div><Instagram size={18} /><strong>{account.handle}</strong><span>{account.label}</span><small>Instagram’da aç <ArrowUpRight size={14} /></small></div></a>)}
          </div>
        </section>
      </section>
      <footer data-testid="site-footer"><span>Eylül Fotoğrafçılık</span><p>© {new Date().getFullYear()} · Ankara</p><a href="#anasayfa" data-testid="back-to-top-link">Başa dön <ArrowUpRight size={15} /></a></footer>
      <a className="floating-whatsapp" href={whatsapp} target="_blank" rel="noreferrer" data-testid="floating-whatsapp-button"><MessageCircle size={20} /> <span>WhatsApp</span></a>
    </main>
  );
}